import React, { useEffect, useState } from 'react';
import { ArrowRight, Sparkles, Clock, Target, Zap } from 'lucide-react';
import { stats } from '../mock';

const examBadges = ['SSC', 'UPSC', 'Banking', 'Railway', 'Defence', 'JEE', 'NEET'];

export default function Hero() {
  const [time, setTime] = useState({ h: 23, m: 45, s: 30 });

  useEffect(() => {
    const t = setInterval(() => {
      setTime((prev) => {
        let { h, m, s } = prev;
        s -= 1;
        if (s < 0) { s = 59; m -= 1; }
        if (m < 0) { m = 59; h -= 1; }
        if (h < 0) { h = 23; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <section className="relative pt-28 pb-20 overflow-hidden">
      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-teal-400/10 blur-3xl" />
      </div>

      {/* Decorative icons */}
      <Target className="absolute left-6 top-1/2 text-emerald-500/20" size={80} strokeWidth={1} />
      <Sparkles className="absolute right-8 top-24 text-emerald-500/20" size={70} strokeWidth={1} />

      <div className="relative max-w-6xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 text-xs font-semibold">
          <Sparkles size={14} />
          50,000+ Students Trust Us
        </div>

        <h1 className="mt-8 font-extrabold tracking-tight leading-[1.05] text-5xl md:text-7xl lg:text-8xl">
          <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">Master Every</span>
          <br />
          <span className="text-white">Government Exam</span>
          <br />
          <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">With ₹499</span>
        </h1>

        <p className="mt-8 max-w-2xl mx-auto text-slate-400 text-lg leading-relaxed">
          India&apos;s most advanced test series platform. AI-powered analytics, real exam interface, and 50+ premium test series at unbeatable prices.
        </p>

        {/* Exam category badges */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          {examBadges.map((e) => (
            <span
              key={e}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/15 transition-colors"
            >
              {e}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            onClick={() => document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' })}
            className="group inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold px-7 py-3.5 rounded-md transition-all shadow-lg shadow-emerald-500/25"
          >
            Get Started
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <div className="inline-flex items-center gap-1.5 text-slate-400 text-sm">
            <Zap size={14} className="text-emerald-400 fill-emerald-400" />
            Instant Login Credentials via Email/SMS within 2 mins
          </div>
        </div>

        {/* Countdown */}
        <div className="mt-10 inline-flex flex-col items-center gap-3 px-8 py-4 rounded-lg border border-rose-500/30 bg-rose-500/5">
          <div className="flex items-center gap-2 text-rose-300 text-sm">
            <Clock size={16} /> Limited Time Offer Ends In:
          </div>
          <div className="flex items-center gap-4">
            <TimeBox value={pad(time.h)} label="Hours" />
            <span className="text-white text-2xl font-bold">:</span>
            <TimeBox value={pad(time.m)} label="Minutes" />
            <span className="text-white text-2xl font-bold">:</span>
            <TimeBox value={pad(time.s)} label="Seconds" />
          </div>
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                {s.value}
              </div>
              <div className="mt-1 text-slate-400 text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TimeBox({ value, label }) {
  return (
    <div className="flex flex-col items-center min-w-[52px]">
      <div className="text-white text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-slate-400 text-xs mt-1">{label}</div>
    </div>
  );
}
